import type { CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { safeHttpUrl } from '@loop3/ui'
import { entityLink } from '@/lib/compute/universe'
import { KIND_META } from '@/lib/entity-kinds'
import type { Entity } from '@/lib/types/universe'

const CARD =
  'group relative flex h-full items-start gap-3 rounded-xl border border-border bg-card p-3.5 transition-colors duration-200 hover:border-[var(--l3-violet)]/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--l3-violet)]'

/** A clickable card for one universe entity: in-app link, or a new tab for external targets. */
export function EntityCard({ entity }: { entity: Entity }) {
  const { icon: Icon, label } = KIND_META[entity.kind]
  const target = entityLink(entity)
  const href = 'href' in target ? safeHttpUrl(target.href) : null

  const body = (
    <>
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[var(--l3-violet-soft)] text-[var(--l3-violet)] transition-colors group-hover:bg-[var(--l3-violet)] group-hover:text-white">
        <Icon className="size-4" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium transition-colors group-hover:text-[var(--l3-violet)]">
          {entity.title}
        </span>
        {entity.subtitle && (
          <span className="mt-0.5 block truncate text-xs opacity-60">
            {entity.subtitle}
          </span>
        )}
        <span className="mt-1.5 block font-mono text-[0.6rem] uppercase tracking-[0.14em] opacity-40">
          {label}
        </span>
      </span>
      <ArrowUpRight
        aria-hidden
        className="size-3.5 shrink-0 opacity-0 transition-all duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-60"
      />
    </>
  )

  if ('to' in target)
    return (
      <Link to={target.to} className={CARD}>
        {body}
      </Link>
    )
  if (href)
    return (
      <a href={href} target="_blank" rel="noreferrer" className={CARD}>
        {body}
      </a>
    )
  return <div className={CARD}>{body}</div>
}

/** Responsive grid of entity cards that fade up in a short stagger. `className` overrides the column steps. */
export function EntityGrid({
  items,
  className = 'sm:grid-cols-2 lg:grid-cols-3',
}: {
  items: Entity[]
  className?: string
}) {
  return (
    <ul className={`grid gap-3 ${className}`}>
      {items.map((e, i) => (
        <li
          key={`${e.kind}:${e.id ?? e.href ?? e.title}`}
          className="bui-motion-card min-w-0"
          style={
            {
              animation: 'bui-fade-up 320ms cubic-bezier(.2,.8,.2,1) both',
              animationDelay: `${Math.min(i, 12) * 25}ms`,
            } as CSSProperties
          }
        >
          <EntityCard entity={e} />
        </li>
      ))}
    </ul>
  )
}
